<script setup>

import { computed } from 'vue';
import Modal from '@/components/modals/Modal.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    profile: {
        type: Object,
        required: true,
        default: () => ({
            avatar: '',
            name: '',
            verified: false,
            communityCreator: false,
            title: '',
            username: '',
            credentials: [],
            socials: []
        })
    }
});

const emit = defineEmits(['close']);

const handleClose = () => {
    emit('close');
};

const socialsURLs = {
    x: 'https://x.com/',
    huggingface: 'https://huggingface.co/',
    github: 'https://github.com/',
    homepage: ''
};

const socialsIcons = {
    x: 'uil uil-twitter',
    huggingface: 'uil uil-robot',
    github: 'uil uil-github',
    homepage: 'uil uil-globe'
};

const getSocialUrl = (social) => {
    if (!social?.value) return null;
    if (social.name === 'homepage') {
        const value = social.value.trim();
        if (!value) return null;
        return /^https?:\/\//i.test(value) ? value : `https://${value}`;
    }
    const baseUrl = socialsURLs[social.name];
    return baseUrl ? `${baseUrl}${social.value}` : null;
};

const getSocialLabel = (socialName) => {
    if (socialName === 'x') return '𝕏';
    return socialName;
};

const validSocials = computed(() => {
    return (props.profile.socials || [])
        .map(social => ({ ...social, url: getSocialUrl(social) }))
        .filter(social => social.url);
});

function getBadgeInitials(credential) {
    if (!credential.name) return '??';
    
    const name = credential.name.trim();
    
    if (name.includes(' ')) {
        return name
            .split(' ')
            .filter(word => word.length > 0)
            .map(word => word.charAt(0).toUpperCase())
            .join('')
            .slice(0, 2);
    }
    
    return name.slice(0, 2).toUpperCase();
}
</script>

<template>
    <Modal 
        :isOpen="isOpen" 
        @close="handleClose"
        size="4xl"
        :closeOnClickOutside="true"
        :showHeader="false"
        bgVariant="dark"
    >
        <div class="space-y-6 m-3">
            <!-- Header with photo, name and information -->
            <div class="relative pb-6">
                <div class="flex items-center gap-4">
                    <img v-if="profile.avatar"
                        :src="profile.avatar" 
                            :alt="profile.name"
                            class="w-20 h-20 rounded-full object-cover border border-echoes-border-gray"
                        />
                    <div v-else class="w-20 h-20 rounded-full object-cover border border-echoes-border-gray bg-echoes-background-gray flex items-center justify-center">
                        <i class="uil uil-user text-white text-2xl"></i>
                    </div>
                    <div class="flex-1 space-y-1">
                        <div class="flex items-center gap-3">
                            <h2 class="text-2xl font-semibold text-white">{{ profile.name }}</h2>
                            <span 
                                v-if="profile.verified"
                                class="flex items-center gap-1.5 px-3 py-0.5 bg-emerald-500/10 rounded-full text-emerald-500 border border-emerald-500/50 text-sm font-medium"
                            >
                                <i class="uil uil-check-circle"></i>
                                Verified
                            </span>
                            <span
                                v-else-if="profile.communityCreator"
                                class="flex items-center gap-1.5 px-3 py-0.5 bg-sky-500/10 rounded-full text-sky-400 border border-sky-500/40 text-sm font-medium"
                            >
                                <i class="uil uil-users-alt"></i>
                                Community creator
                            </span>
                        </div>
                        <p class="text-white/80">
                            {{ profile.title }}
                            <span v-if="profile.username" class="text-white/80"> • @{{ profile.username }}</span>
                        </p>
                    </div>
                </div>
                <div class="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-gray to-transparent"></div>
            </div>
            <!-- Credentials & Badges -->
            <div>
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-xl font-semibold text-white">Credentials & Badges</h3>
                    <span class="text-white/80 text-sm">{{ profile.credentials?.length || 0 }} badges</span>
                </div>

                <!-- Empty State -->
                <div 
                    v-if="!profile.credentials || profile.credentials.length === 0"
                    class="flex flex-col items-center justify-center py-12 px-4"
                >
                    <div class="w-20 h-20 rounded-full bg-echoes-background-gray/50 border border-echoes-border-gray flex items-center justify-center mb-4">
                        <i class="uil uil-award text-white/60 text-4xl"></i>
                    </div>
                    <p class="text-white/80 text-center text-sm">No badges to show</p>
                </div>

                <!-- Grid of Credentials -->
                <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                    <div 
                        v-for="(credential, index) in profile.credentials" 
                        :key="index"
                        class="flex items-center gap-4 p-4 bg-echoes-background-gray/50 border border-echoes-border-gray rounded-2xl hover:border-echoes-border-hover transition-colors duration-200"
                    >
                        <!-- Badge Image or Initials -->
                        <div 
                            class="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border border-echoes-border-light"
                            :class="credential.badge_image ? '' : (!credential.badge_color ? 'bg-echoes-background-gray' : '')"
                            :style="!credential.badge_image && credential.badge_color ? { backgroundColor: credential.badge_color } : {}"
                        >
                            <img 
                                v-if="credential.badge_image" 
                                :src="credential.badge_image" 
                                :alt="credential.name || 'Badge'" 
                                class="w-full h-full object-cover rounded-full" 
                            />
                            <span v-else class="text-white font-semibold text-sm">{{ getBadgeInitials(credential) }}</span>
                        </div>
                        <!-- Information -->
                        <div class="flex-1 min-w-0">
                            <h4 class="text-white font-medium text-sm mb-0.5 truncate">{{ credential.name }}</h4>
                            <p class="text-white/80 text-xs truncate">{{ credential.description }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Socials -->
            <div v-if="validSocials.length > 0">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-xl font-semibold text-white">Socials</h3>
                    <span class="text-white/80 text-sm">{{ validSocials.length }} links</span>
                </div>
                <div class="flex flex-wrap gap-3">
                    <a
                        v-for="(social, index) in validSocials"
                        :key="index"
                        :href="social.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex items-center gap-2 px-4 py-2 bg-echoes-background-gray/50 border border-echoes-border-gray rounded-full text-white/90 hover:text-white hover:border-echoes-border-hover transition-colors duration-200"
                    >
                        <i
                            v-if="social.name !== 'x'"
                            :class="socialsIcons[social.name] || 'uil uil-link-h'"
                        ></i>
                        <span class="text-sm capitalize">{{ getSocialLabel(social.name) }}</span>
                    </a>
                </div>
            </div>

            <!-- Footer with verification -->
            <div class="relative pt-4">
                <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-echoes-border-gray to-transparent"></div>
                <div class="flex items-center justify-between pt-2">
                    <p class="text-white/80 text-sm">
                        <template v-if="profile.verified">
                            Credentials have been independently verified for this expert.
                        </template>
                        <template v-else-if="profile.communityCreator">
                            This creator joined through self-serve onboarding and is not an invited expert.
                        </template>
                        <template v-else>
                            Profile information is provided by the creator.
                        </template>
                    </p>
                    <button 
                        @click="handleClose"
                        class="px-4 py-2 bg-transparent border border-white text-white rounded-full text-sm hover:text-echoes-background-gray hover:bg-white transition-colors duration-200"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    </Modal>
</template>

